import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Percent, Users, Trash2, Copy, Pencil, UserPlus, FileText, Download, DollarSign } from "lucide-react";
import { api, formatApiErrorDetail } from "../api";
import { PageHeader, Btn, Badge, eur } from "../components/common";
import { Modal, Field, TextArea, Selecter } from "./crm/crmui";
import { useAutoRefresh } from "../lib/autoRefresh";

const EMPTY = { nome: "", email: "", telefone: "", nif: "", taxa_percentual: 10, modelo: "unica", duracao_meses: 0, ativo: true, notas: "", website: "", morada: "", is_billing_entity: false, comissao_default_pct: 20 };
const MODELOS = [
  { value: "unica", label: "Comissao unica (primeira venda)" },
  { value: "recorrente", label: "Comissao recorrente (por renovacao)" },
];

const ESTADOS_COM = ["pendente", "aprovada", "paga", "anulada"];

export default function Partners() {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const [selPartner, setSelPartner] = useState(null);
  const [comissoes, setComissoes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [portalUser, setPortalUser] = useState(null);
  const [statement, setStatement] = useState(null);        // {partner, aging, mrr, ...}
  const [emitFor, setEmitFor] = useState(null);            // {partner_id, ano, mes}

  const load = async () => {
    try {
      const { data } = await api.get("/partners/summary/all");
      setRows(data || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);
  useAutoRefresh(load);

  const loadComissoes = async (pid) => {
    try {
      const { data } = await api.get(`/partners/${pid}/commissions`);
      setComissoes(data || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const salvar = async () => {
    if (!edit.nome.trim()) return toast.error("Indique o nome.");
    setBusy(true);
    try {
      if (edit.id) {
        await api.put(`/partners/${edit.id}`, edit);
      } else {
        await api.post("/partners", edit);
      }
      toast.success("Guardado."); setEdit(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remover = async (p) => {
    if (!confirm(`Eliminar parceiro "${p.nome}"?`)) return;
    try {
      await api.delete(`/partners/${p.id}`);
      toast.success("Eliminado.");
      if (selPartner?.id === p.id) setSelPartner(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const setEstado = async (c, estado) => {
    try {
      await api.put(`/partners/commissions/${c.id}`, { estado });
      toast.success("Comissao atualizada.");
      loadComissoes(selPartner.id); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const copyRefLink = async (p) => {
    const url = `${window.location.origin}/f/partner-${p.codigo}`;
    try { await navigator.clipboard.writeText(url); toast.success("Link copiado."); } catch {}
  };

  const openStatement = async (p) => {
    try {
      const { data } = await api.get(`/partners/${p.id}/statement`);
      setStatement(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const downloadStatementPdf = async (pid) => {
    try {
      const resp = await api.get(`/partners/${pid}/statement.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `extrato-${pid.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const emitMonthly = async () => {
    if (!emitFor?.partner_id || !emitFor.ano || !emitFor.mes) return;
    try {
      const { data } = await api.post(`/partners/${emitFor.partner_id}/commissions/emit-monthly`,
        { ano: parseInt(emitFor.ano), mes: parseInt(emitFor.mes), notas: emitFor.notas || null });
      toast.success(`Comissao mensal emitida - Total ${data.total} EUR`);
      setEmitFor(null);
      if (selPartner?.id === emitFor.partner_id) loadComissoes(selPartner.id);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const criarPortalUser = async () => {
    if (!portalUser?.nome?.trim() || !portalUser?.email?.trim()) return toast.error("Nome e email obrigatorios.");
    try {
      const { data } = await api.post(`/partners/${portalUser.partner_id}/portal-user`, {
        nome: portalUser.nome, email: portalUser.email, password: portalUser.password || "",
      });
      if (data.password_gerada) {
        try { await navigator.clipboard.writeText(data.password_gerada); } catch {}
        toast.success(`Utilizador criado. Password copiada: ${data.password_gerada}`);
      } else {
        toast.success("Utilizador do portal criado.");
      }
      setPortalUser(null);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const totalPendente = rows.reduce((s, r) => s + (r.pendente || 0), 0);
  const totalPaga = rows.reduce((s, r) => s + (r.paga || 0), 0);

  return (
    <>
      <PageHeader
        overline="Comercial"
        title="Parceiros e comissoes"
        action={<Btn onClick={() => setEdit({ ...EMPTY })} testid="new-partner-btn"><Plus size={16} /> Novo parceiro</Btn>}
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="partners-stats">
        <div className="border border-tint rounded-lg p-3 bg-white"><div className="text-[10px] uppercase text-muted2">Parceiros</div><div className="text-2xl font-bold text-navy">{rows.length}</div></div>
        <div className="border border-tint rounded-lg p-3 bg-white"><div className="text-[10px] uppercase text-muted2">Leads via parceiros</div><div className="text-2xl font-bold text-navy">{rows.reduce((s, r) => s + (r.n_leads || 0), 0)}</div></div>
        <div className="border border-tint rounded-lg p-3 bg-white"><div className="text-[10px] uppercase text-muted2">Comissao pendente</div><div className="text-2xl font-bold text-amber-600">{eur(totalPendente)}</div></div>
        <div className="border border-tint rounded-lg p-3 bg-white"><div className="text-[10px] uppercase text-muted2">Comissao paga</div><div className="text-2xl font-bold text-emerald-600">{eur(totalPaga)}</div></div>
      </div>

      {rows.length === 0 && <div className="text-center text-muted2 border border-tint rounded-lg py-12">Sem parceiros. Crie o primeiro para comecar a gerir comissoes de referral.</div>}

      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3" data-testid="partners-grid">
        {rows.map((p) => (
          <div key={p.id} data-testid={`partner-${p.id}`} className={`border rounded-lg p-3 bg-white ${p.ativo ? "border-tint hover:shadow-sm" : "border-slate-200 opacity-60"}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-navy truncate">{p.nome}</div>
                <div className="text-[10px] font-mono text-blue">codigo: {p.codigo}</div>
              </div>
              <button onClick={() => copyRefLink(p)} title="Copiar link de referral" className="p-1 text-slate-500 hover:text-blue"><Copy size={13} /></button>
            </div>
            <div className="text-[11px] text-slate-500 mt-1 flex flex-wrap gap-1.5 items-center">
              <span className="inline-flex items-center gap-0.5"><Percent size={9} /> {p.taxa_percentual}%</span>
              <Badge value={p.modelo} />
              {!p.ativo && <span className="text-[9px] px-1 py-0.5 rounded bg-slate-100 text-slate-500">inativo</span>}
            </div>
            <div className="grid grid-cols-3 gap-1 mt-2 text-center">
              <div><div className="text-[9px] uppercase text-muted2">Leads</div><div className="text-sm font-bold text-navy">{p.n_leads || 0}</div></div>
              <div><div className="text-[9px] uppercase text-muted2">Clientes</div><div className="text-sm font-bold text-navy">{p.n_clientes || 0}</div></div>
              <div><div className="text-[9px] uppercase text-muted2">Pendente</div><div className="text-sm font-bold text-amber-600">{eur(p.pendente || 0)}</div></div>
            </div>
            <div className="flex items-center gap-1 mt-2 pt-2 border-t border-tint">
              <button onClick={() => { setSelPartner(p); loadComissoes(p.id); }} data-testid={`partner-view-${p.id}`}
                className="text-[11px] px-2 py-1 rounded bg-blue text-white hover:bg-[var(--blue-hover)] inline-flex items-center gap-1"><Users size={11} /> Comissoes</button>
              {p.is_billing_entity && (
                <button onClick={() => openStatement(p)} data-testid={`partner-statement-${p.id}`}
                  title="Extrato / Aging" className="text-[11px] px-2 py-1 rounded bg-amber-600 text-white hover:bg-amber-700 inline-flex items-center gap-1"><FileText size={11} /> Extrato</button>
              )}
              <button onClick={() => setPortalUser({ partner_id: p.id, nome: "", email: "", password: "" })} data-testid={`partner-portal-${p.id}`}
                title="Criar acesso ao portal parceiro" className="text-[11px] px-2 py-1 rounded border border-tint text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1"><UserPlus size={11} /></button>
              <button onClick={() => setEdit({ ...p })} data-testid={`partner-edit-${p.id}`}
                className="text-[11px] px-2 py-1 rounded border border-tint text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1"><Pencil size={11} /></button>
              <button onClick={() => remover(p)} data-testid={`partner-delete-${p.id}`}
                className="text-[11px] px-2 py-1 rounded border border-red-200 text-red-600 hover:bg-red-50"><Trash2 size={11} /></button>
            </div>
          </div>
        ))}
      </div>

      <Modal open={!!edit} onClose={() => setEdit(null)} title={edit?.id ? `Editar ${edit?.nome}` : "Novo parceiro"} testid="partner-modal" size="lg">
        {edit && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Nome" value={edit.nome} onChange={(v) => setEdit({ ...edit, nome: v })} testid="partner-nome" />
              <Field label="Email" value={edit.email} onChange={(v) => setEdit({ ...edit, email: v })} testid="partner-email" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Telefone" value={edit.telefone} onChange={(v) => setEdit({ ...edit, telefone: v })} testid="partner-telefone" />
              <Field label="NIF" value={edit.nif} onChange={(v) => setEdit({ ...edit, nif: v })} testid="partner-nif" />
              <Field label="Website" value={edit.website} onChange={(v) => setEdit({ ...edit, website: v })} testid="partner-website" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Taxa % (referral)" type="number" value={edit.taxa_percentual} onChange={(v) => setEdit({ ...edit, taxa_percentual: parseFloat(v) || 0 })} testid="partner-taxa" />
              <Selecter label="Modelo" value={edit.modelo} onChange={(v) => setEdit({ ...edit, modelo: v })} options={MODELOS} testid="partner-modelo" />
              <Field label="Duracao (meses, 0=ilimitado)" type="number" value={edit.duracao_meses} onChange={(v) => setEdit({ ...edit, duracao_meses: parseInt(v) || 0 })} testid="partner-duracao" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Morada de faturacao" value={edit.morada || ""} onChange={(v) => setEdit({ ...edit, morada: v })} testid="partner-morada" />
              <Field label="Comissao mensal % (MRR)" type="number" value={edit.comissao_default_pct ?? 20} onChange={(v) => setEdit({ ...edit, comissao_default_pct: parseFloat(v) || 0 })} testid="partner-comissao-default" />
            </div>
            <label className="inline-flex items-center gap-2 text-sm bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              <input type="checkbox" checked={!!edit.is_billing_entity} onChange={(e) => setEdit({ ...edit, is_billing_entity: e.target.checked })} data-testid="partner-is-billing" />
              <span><b>Entidade de faturacao</b> - contas ligadas a este parceiro sao faturadas com estes dados fiscais (revenda). Se off, funciona so como referral.</span>
            </label>
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!edit.ativo} onChange={(e) => setEdit({ ...edit, ativo: e.target.checked })} data-testid="partner-ativo" />
              Ativo
            </label>
            <TextArea label="Notas" value={edit.notas} onChange={(v) => setEdit({ ...edit, notas: v })} testid="partner-notas" />
            <div className="flex justify-end gap-2 pt-2">
              <Btn variant="ghost" onClick={() => setEdit(null)}>Cancelar</Btn>
              <Btn onClick={salvar} testid="partner-save" disabled={busy}>{busy ? "..." : "Guardar"}</Btn>
            </div>
          </>
        )}
      </Modal>

      <Modal open={!!selPartner} onClose={() => setSelPartner(null)} title={`Comissoes - ${selPartner?.nome || ""}`} testid="partner-com-modal" size="lg">
        {selPartner && (
          <>
            <div className="text-xs text-muted2 mb-2">Codigo de referral: <span className="font-mono font-bold text-blue">{selPartner.codigo}</span></div>
            {comissoes.length === 0 && <div className="text-sm text-muted2 py-6 text-center">Sem comissoes registadas.</div>}
            <div className="space-y-1.5 max-h-[60vh] overflow-y-auto">
              {comissoes.map((c) => (
                <div key={c.id} data-testid={`com-${c.id}`} className="border border-tint rounded-md p-2.5 bg-white">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-navy truncate">{c.account_nome}</div>
                      <div className="text-[11px] text-muted2">{c.origem} - {c.ref_id} - base {eur(c.valor_base)}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-navy">{eur(c.valor)}</div>
                      <div className="text-[10px] text-muted2">{c.taxa_percentual}%</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 mt-2">
                    {ESTADOS_COM.map((e) => (
                      <button key={e} onClick={() => setEstado(c, e)} data-testid={`com-${c.id}-${e}`}
                        className={`text-[10px] px-2 py-0.5 rounded ${c.estado === e ? "bg-navy text-white" : "border border-tint text-slate-600 hover:bg-slate-50"}`}>
                        {e}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </Modal>
      <Modal open={!!portalUser} onClose={() => setPortalUser(null)} title="Criar acesso ao Portal do Parceiro" testid="portal-user-modal">
        {portalUser && (
          <>
            <p className="text-xs text-muted2">O parceiro fara login em /partner com este email. Se deixar a password em branco, sera gerada e copiada para a area de transferencia.</p>
            <Field label="Nome" value={portalUser.nome} onChange={(v) => setPortalUser({ ...portalUser, nome: v })} testid="pu-nome" />
            <Field label="Email" value={portalUser.email} onChange={(v) => setPortalUser({ ...portalUser, email: v })} testid="pu-email" />
            <Field label="Password (opcional)" type="password" value={portalUser.password} onChange={(v) => setPortalUser({ ...portalUser, password: v })} testid="pu-pwd" />
            <div className="flex justify-end gap-2 pt-2">
              <Btn variant="ghost" onClick={() => setPortalUser(null)}>Cancelar</Btn>
              <Btn onClick={criarPortalUser} testid="pu-save">Criar acesso</Btn>
            </div>
          </>
        )}
      </Modal>

      <Modal open={!!statement} onClose={() => setStatement(null)}
        title={`Extrato - ${statement?.partner?.nome || ""}`} testid="statement-modal" size="lg">
        {statement && (
          <div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
              <div className="bg-white border border-tint rounded-lg p-3">
                <div className="text-[9px] uppercase text-muted2">MRR</div>
                <div className="text-lg font-bold text-navy">{eur(statement.mrr)}</div>
                <div className="text-[10px] text-muted2">{statement.subs_ativas} sub(s) ativa(s)</div>
              </div>
              <div className="bg-white border border-tint rounded-lg p-3">
                <div className="text-[9px] uppercase text-muted2">Comissao/mes</div>
                <div className="text-lg font-bold text-emerald-700">{eur(statement.comissao_mensal_estimada)}</div>
                <div className="text-[10px] text-muted2">estimada s/ MRR</div>
              </div>
              <div className="bg-white border border-tint rounded-lg p-3">
                <div className="text-[9px] uppercase text-muted2">Em aberto</div>
                <div className="text-lg font-bold text-amber-700">{eur(statement.total_em_aberto)}</div>
              </div>
              <div className="bg-white border border-tint rounded-lg p-3">
                <div className="text-[9px] uppercase text-muted2">Contas</div>
                <div className="text-lg font-bold text-navy">{statement.accounts?.length || 0}</div>
              </div>
            </div>

            <h4 className="text-xs uppercase tracking-wide text-muted2 font-bold mb-2">Aging</h4>
            <div className="grid grid-cols-4 gap-2 mb-4">
              {["0-30", "31-60", "61-90", "90+"].map((b) => {
                const v = statement.aging?.[b] || 0;
                const tone = b === "90+" ? "text-red-700 bg-red-50 border-red-200" :
                              b === "61-90" ? "text-amber-700 bg-amber-50 border-amber-200" :
                              b === "31-60" ? "text-navy bg-slate-50 border-tint" :
                              "text-emerald-700 bg-emerald-50 border-emerald-200";
                return (
                  <div key={b} className={`border rounded-lg p-2 ${tone}`} data-testid={`aging-${b}`}>
                    <div className="text-[9px] uppercase">{b} dias</div>
                    <div className="text-sm font-bold">{eur(v)}</div>
                  </div>
                );
              })}
            </div>

            {statement.open_invoices?.length > 0 && (
              <>
                <h4 className="text-xs uppercase tracking-wide text-muted2 font-bold mb-2">Faturas em aberto</h4>
                <div className="max-h-56 overflow-y-auto border border-tint rounded-lg">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left p-2">Numero</th>
                        <th className="text-left p-2">Data</th>
                        <th className="text-left p-2">Subscritor</th>
                        <th className="text-right p-2">Total</th>
                        <th className="text-right p-2">Dias</th>
                      </tr>
                    </thead>
                    <tbody>
                      {statement.open_invoices.map((inv, i) => (
                        <tr key={i} className="border-t border-tint">
                          <td className="p-2 font-mono">{inv.numero || (inv.id || "").slice(0, 8)}</td>
                          <td className="p-2 text-muted2">{(inv.data_emissao || "").slice(0, 10)}</td>
                          <td className="p-2">{inv.subscritor_original || inv.organizacao_nome || "-"}</td>
                          <td className="p-2 text-right font-semibold">{eur(inv.total || 0)}</td>
                          <td className={`p-2 text-right font-mono ${inv.dias > 90 ? "text-red-600 font-bold" : inv.dias > 60 ? "text-amber-600" : ""}`}>{inv.dias}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            <div className="flex justify-between gap-2 pt-4 mt-4 border-t border-tint">
              <Btn variant="ghost" onClick={() => setEmitFor({ partner_id: statement.partner.id, ano: new Date().getFullYear(), mes: new Date().getMonth() + 1, notas: "" })} testid="emit-monthly-btn">
                <DollarSign size={14} /> Emitir comissao mensal
              </Btn>
              <div className="flex gap-2">
                <Btn variant="ghost" onClick={() => downloadStatementPdf(statement.partner.id)} testid="download-pdf-btn">
                  <Download size={14} /> Descarregar PDF
                </Btn>
                <Btn onClick={() => setStatement(null)}>Fechar</Btn>
              </div>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={!!emitFor} onClose={() => setEmitFor(null)}
        title="Emitir comissao mensal" testid="emit-modal">
        {emitFor && (
          <>
            <p className="text-xs text-muted2 mb-3">
              Cria um registo de comissao para o periodo indicado. Valor calculado como
              soma de (MRR x %) das subscricoes ativas do parceiro.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Ano" type="number" value={emitFor.ano} onChange={(v) => setEmitFor({ ...emitFor, ano: v })} testid="emit-ano" />
              <Field label="Mes (1-12)" type="number" value={emitFor.mes} onChange={(v) => setEmitFor({ ...emitFor, mes: v })} testid="emit-mes" />
            </div>
            <Field label="Notas (opcional)" value={emitFor.notas || ""} onChange={(v) => setEmitFor({ ...emitFor, notas: v })} testid="emit-notas" />
            <div className="flex justify-end gap-2 pt-2">
              <Btn variant="ghost" onClick={() => setEmitFor(null)}>Cancelar</Btn>
              <Btn onClick={emitMonthly} testid="emit-save">Emitir</Btn>
            </div>
          </>
        )}
      </Modal>
    </>
  );
}
